import React, { useContext } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import Tooltip from '@totem/components/common/tooltip/Tooltip';
import EndOfLifeContext from '@totem/components/endoflife/EndOfLifeContext';
import { Product } from '@totem/components/endoflife/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const styles = {
  iconButton: {
    margin: '0 2px',
  },
};

const EndOfLifeTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { loading, reportData, totalRecords, onAction } =
    useContext(EndOfLifeContext);
  const { input, handleTableChange } = useContext(InputContext);

  const getData = () => {
    if (reportData !== null) {
      if (reportData.products !== null) {
        return reportData.products;
      }
    }
    return [];
  };

  const startEdit = (product: Product) => {
    onAction('edit', product);
  };

  const columns: ColumnProps<Product>[] = [
    {
      title: 'Product Type',
      dataIndex: 'productType',
      key: 'productType',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.productType === compB.productType
          ? 0
          : compA.productType > compB.productType
            ? 1
            : -1,
    },
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.product === compB.product
          ? 0
          : compA.product > compB.product
            ? 1
            : -1,
    },
    {
      title: 'Cycle',
      dataIndex: 'cycle',
      key: 'cycle',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.cycle === compB.cycle ? 0 : compA.cycle > compB.cycle ? 1 : -1,
    },
    {
      title: 'Latest Version',
      dataIndex: 'latestVersion',
      key: 'latestVersion',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.latestVersion === compB.latestVersion
          ? 0
          : compA.latestVersion > compB.latestVersion
            ? 1
            : -1,
    },
    {
      title: 'End of Support',
      dataIndex: 'endOfSupportDate',
      key: 'endOfSupportDate',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.endOfSupportDate === compB.endOfSupportDate
          ? 0
          : compA.endOfSupportDate > compB.endOfSupportDate
            ? 1
            : -1,
    },
    {
      title: 'End of Life',
      dataIndex: 'endOfLifeDate',
      key: 'endOfLifeDate',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.endOfLifeDate === compB.endOfLifeDate
          ? 0
          : compA.endOfLifeDate > compB.endOfLifeDate
            ? 1
            : -1,
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'action',
      render: (_, item: Product) => (
        <div className="center-table-cell">
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Update Building Mapping" placement="top">
              <Button
                type="default"
                icon={<EditOutlined />}
                shape="circle"
                onClick={() => startEdit(item)}
                style={styles.iconButton}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getData()}
      loading={loading}
      onChange={(
        updatedPagination: TablePaginationConfig,
        filters,
        sorter: SorterResult<Product>,
        extra,
      ) => handleTableChange(updatedPagination, filters, sorter, extra)}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.id}
    />
  );
};

export default EndOfLifeTable;
